import type { JSX } from 'react';
import type { WireSourceHealthRow, WireSourceInstance, WireSourceKind } from '@logistics/contracts';
import { ago } from '@/lib/format';
import { cn } from '@/lib/utils';
import { sourceGlyph, sourceKindName } from '@/lib/glyphs';
import { secretCount, sourceStatus } from './credentials';

/** One connected source (design L373–387): glyph, status, name, type, credentials, last sync. */
export function SourceCard({
  instance,
  descriptor,
  health,
  onOpen,
}: {
  instance: WireSourceInstance;
  descriptor: WireSourceKind | undefined;
  health: WireSourceHealthRow | undefined;
  onOpen: () => void;
}): JSX.Element {
  const status = sourceStatus(instance, health);
  const synced = health?.lastSyncAt === undefined ? 'never' : ago(health.lastSyncAt);
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'bg-surf hover:border-acc flex flex-col gap-3.5 rounded-2xl border p-5 text-left text-inherit transition-[border-color,transform] duration-200 hover:-translate-y-0.5',
        status.failing ? 'border-bad' : 'border-line',
      )}
    >
      <div className="flex items-start justify-between">
        <SourceGlyph kind={instance.kind} />
        <StatusMark color={status.color} label={status.label} />
      </div>
      <div className="min-w-0">
        <div className="truncate text-[15px] font-semibold">{instance.label}</div>
        <div className="text-mute mt-0.5 text-[13px]">{sourceKindName(instance.kind)}</div>
      </div>
      <div className="border-line text-mute flex justify-between border-t pt-3 font-mono text-[11px]">
        <span>{secretCount(descriptor)} credentials</span>
        <span>synced {synced}</span>
      </div>
    </button>
  );
}

/** The 44px mono tile of a source card and the side panel header. */
export function SourceGlyph({ kind }: { kind: string }): JSX.Element {
  return (
    <span className="bg-surf2 border-line grid size-11 flex-none place-items-center rounded-xl border font-mono text-xs font-medium">
      {sourceGlyph(kind)}
    </span>
  );
}

/** "● Connected", with the design's glow on the dot. */
export function StatusMark({ color, label }: { color: string; label: string }): JSX.Element {
  return (
    <span className="flex items-center gap-1.5 font-mono text-xs" style={{ color }}>
      <span
        className="size-[7px] rounded-full"
        style={{ background: color, boxShadow: `0 0 8px ${color}` }}
      />
      {label}
    </span>
  );
}

/** The dashed "+ Connect another source" tile at the end of the grid. */
export function ConnectAnother({ onClick }: { onClick: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="border-line text-mute hover:text-acc grid min-h-[170px] place-items-center rounded-2xl border border-dashed bg-transparent p-5 text-sm transition-colors duration-200 hover:border-acc"
    >
      + Connect another source
    </button>
  );
}
